import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Avatar from '../Avatar'
import Button from '../Button'
import DeltaPill from './DeltaPill'
import Icon from '../icon/Icon'
import Text from '../Text'

// Utils
import { useAppState } from '../../context/AppContext'
import { personaKeyForScores } from './InsightsMatrix'

// Types
import { ICompanyMember, ICompanyTerm } from '../../common/models/Company'

interface IProps {
  member: ICompanyMember
  cultureScore: number
  skillScore: number
  cultureAssessed?: boolean
  skillAssessed?: boolean
  previousCultureScore?: number
  previousSkillScore?: number
  compareActive?: boolean
  selectedTerm?: ICompanyTerm
  compareTo?: ICompanyTerm
  onClose: () => void
  onBack?: () => void
  onShowDetails?: () => void
}

const InsightsMatrixMemberPopover: React.FC<IProps> = ({
  member,
  cultureScore,
  skillScore,
  cultureAssessed = true,
  skillAssessed = true,
  previousCultureScore,
  previousSkillScore,
  compareActive,
  onClose,
  onBack,
  onShowDetails,
}) => {
  const { t } = useTranslation()
  const { theme, company } = useAppState()

  const personaKey = personaKeyForScores(skillScore, cultureScore)
  // The persona blends both axes, so it only makes sense when both were assessed.
  const showPersona = skillAssessed && cultureAssessed
  const { firstName, preposition, lastName } = member

  const functionLabel = member.jobLevel?.title

  const cultureRankId = member.jobLevel?.cultureRankId
  const cultureRankTitle =
    cultureRankId && company?.cultureRanks && company.cultureRanks.length > 1
      ? company?.cultureRanks?.find((r) => r._id === cultureRankId)?.title
      : undefined

  return (
    <Container>
      <Header>
        {onBack && (
          <HeaderIconButton
            colorVariant="tertiary"
            icon="ChevronLeft"
            iconSize={14}
            onClick={onBack}
          />
        )}
        <AvatarSlot>
          <Avatar user={member} />
        </AvatarSlot>
        <NameWrapper>
          <Text textStyle="labelHeavy" color="text" tag="span">
            {firstName}
          </Text>
          {lastName && (
            <Text textStyle="label" color="textLight" tag="span">
              {preposition ? `${preposition} ${lastName}` : lastName}
            </Text>
          )}
        </NameWrapper>
        <CloseButton
          colorVariant="tertiary"
          icon="Close"
          iconSize={12}
          onClick={onClose}
        />
      </Header>
      <ScoreGrid>
        <ScoreColumn>
          <ScoreLabel>
            <Icon type="Skills" size={14} color={theme.colors.text} />
            <Text textStyle="labelHeavy" tag="span">
              {t('general.skills')}
            </Text>
          </ScoreLabel>
          <ScoreChip>
            {skillAssessed ? (
              <>
                <Text textStyle="title" color="primary" tag="span">
                  {Math.round(skillScore)}
                </Text>
                <Suffix textStyle="caption" color="textLight" tag="span">
                  /100
                </Suffix>
              </>
            ) : (
              <Text textStyle="title" color="textLight" tag="span">
                -
              </Text>
            )}
          </ScoreChip>
          {!skillAssessed ? (
            <Text textStyle="caption" color="textLight" tag="span">
              {t('general.states.noFinalScore')}
            </Text>
          ) : (
            compareActive && (
              <DeltaPill
                current={skillScore}
                previous={previousSkillScore}
                size="regular"
              />
            )
          )}
        </ScoreColumn>
        <ScoreColumn>
          <ScoreLabel>
            <Icon type="Culture" size={14} color={theme.colors.text} />
            <Text textStyle="labelHeavy" tag="span">
              {t('general.feedback.culture')}
            </Text>
          </ScoreLabel>
          <ScoreChip>
            {cultureAssessed ? (
              <>
                <Text textStyle="title" color="primary" tag="span">
                  {Math.round(cultureScore)}
                </Text>
                <Suffix textStyle="caption" color="textLight" tag="span">
                  /100
                </Suffix>
              </>
            ) : (
              <Text textStyle="title" color="textLight" tag="span">
                -
              </Text>
            )}
          </ScoreChip>
          {!cultureAssessed ? (
            <Text textStyle="caption" color="textLight" tag="span">
              {t('general.states.noFinalScore')}
            </Text>
          ) : (
            compareActive && (
              <DeltaPill
                current={cultureScore}
                previous={previousCultureScore}
                size="regular"
              />
            )
          )}
        </ScoreColumn>
      </ScoreGrid>
      <MetaRows>
        {showPersona && (
          <MetaRow>
            <Icon type="Star" size={14} color={theme.colors.primary} />
            <Text textStyle="bodySmall" color="primary" tag="span">
              {t(personaKey)}
            </Text>
          </MetaRow>
        )}
        {functionLabel && (
          <MetaRow>
            <Icon type="Job" size={14} color={theme.colors.text72} />
            <Text textStyle="bodySmall" color="text" tag="span">
              {functionLabel}
            </Text>
          </MetaRow>
        )}
        {cultureRankTitle && (
          <MetaRow>
            <Icon type="Culture" size={14} color={theme.colors.text72} />
            <Text textStyle="bodySmall" color="text" tag="span">
              {cultureRankTitle}
            </Text>
          </MetaRow>
        )}
      </MetaRows>
      {onShowDetails && (
        <DetailsCta
          caption={t('insights.detailedResults')}
          icon="ArrowRight"
          iconSize={11}
          color="primaryFadeLight"
          textColor="primary"
          hoverColor="primaryLight"
          hoverTextColor="primary"
          switchOrder
          onClick={onShowDetails}
        />
      )}
    </Container>
  )
}

export default InsightsMatrixMemberPopover

const Container = styled.div`
  width: 320px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 -20px;
  padding: 0 20px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.textLighter};
`

const AvatarSlot = styled.div`
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  > figure {
    width: 100%;
    height: 100%;
    margin: 0;
  }
`

const HeaderIconButton = styled(Button)`
  padding: 8px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }
`

const CloseButton = styled(HeaderIconButton)`
  margin-left: auto;
`

const NameWrapper = styled.span`
  flex: 1;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
`

const MetaRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > span:first-child {
    margin-right: 0;
  }
`

const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`

const ScoreColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`

const ScoreLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  > span:first-child {
    margin-right: 0;
  }
`

const Suffix = styled(Text)`
  margin-left: -2px;
`

const ScoreChip = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.primaryFadeLight};
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
`

const DetailsCta = styled(Button)`
  padding: 10px 12px;
  gap: 6px;

  > label {
    margin-right: 0;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
